'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Beam
//***********************************************************************************
//***********************************************************************************

import { cn_element } from './cn_element';
import { cn_add, cn_box, cn_dist, cn_dot, cn_middle, cn_mul, cn_normal, cn_normalize, cn_sub, cnx_add, cnx_mul, cnx_normalize, cnx_sub } from '../utils/cn_utilities';
import { fh_clone, fh_cross, fh_extruded_polygon, fh_matrix, fh_mul, fh_normalize, fh_polygon, fh_sub } from '@acenv/fh-3d-viewer';
import { cn_storey } from './cn_storey';
import { cn_element_visitor } from '../utils/visitors/cn_element_visitor';
import { cn_scene } from './cn_scene';
import { cn_space } from './cn_space';
import { logger } from '../utils/cn_logger';
import { cn_beam_column_type } from './cn_beam_column_type';

/**
 * @class beam
 */
export class cn_beam extends cn_element {
    /**
     *
     * @param {cn_beam_column_type} element_type
     * @param {cn_scene} scene
     */
    constructor(element_type, scene) {
        super(scene);
        this.class_name = 'cn_beam';

        //*** Model data
        this.vertices = [[0, 0, 0], [1, 0, 0]];
        this.element_type = element_type;
        this.facing = null;
        this.substrate = null;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = this.serialize_element();
        json.vertices = [fh_clone(this.vertices[0]), fh_clone(this.vertices[1])];
        json.element_type = this.element_type.ID;
        json.facing = this.facing ? { ID: this.facing.ID } : null;
        json.substrate = this.substrate ? { ID: this.substrate.ID } : null;
        return json;
    }

    static unserialize(json, scene) {
        if (typeof (json.ID) != 'string') return false;
        if (typeof (json.vertices) != 'object') return false;
        if (typeof (json.element_type) != 'string') return false;

        var et = null;
        if (typeof (json.element_type) == 'string')
            et = scene.building.get_element_type(json.element_type);
        if (et == null) {
            logger.log('use default beam type');
            et = scene.building.get_beam_types()[0];
        }

        var beam = new cn_beam(et, scene);
        beam.unserialize_element(json);
        beam.vertices[0] = fh_clone(json.vertices[0]);
        beam.vertices[1] = fh_clone(json.vertices[1]);

        if (json.facing) {
            beam.facing = scene.building.facing_types.find(f => f.ID === json.facing.ID);
        }

        if (json.substrate) {
            beam.substrate = scene.building.get_mmm_compositions().find(f => f.ID === json.facing.ID);
        }

        scene.beams.push(beam);
    }

    /**
     * Retruns a readable ID
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {string}
     */
    build_readable_id(storey) {
        let name = storey.build_readable_id(storey) + "Po";
        return name + (1 + storey.scene.beams.indexOf(this));
    }

    //***********************************************************************************
    //**** Draw
    //***********************************************************************************
    draw(camera, add_classes = [], fill = '') {
        var html = '';
        if (this.status < 0) return html;

        var mouseover = (add_classes.indexOf('mouseover') >= 0);
        var selected = (add_classes.indexOf('selected') >= 0);


        var p0 = camera.world_to_screen(this.vertices[0]);
        var p1 = camera.world_to_screen(this.vertices[1]);
        var th = 0.5 * camera.world_to_screen_scale * this.element_type.width;
        var normal = cn_normal(cn_sub(p1, p0));
        cn_normalize(normal);
        normal = cn_mul(normal, th);

        if (mouseover || selected) {
            html += `<path class="beam_highlight ${(selected) ? 'selected' : 'mouseover'} " d='`;
            html += 'M ' + (p0[0] - normal[0]) + ' ' + (p0[1] - normal[1]) + ' ';
            html += 'L ' + (p0[0] + normal[0]) + ' ' + (p0[1] + normal[1]) + ' ';
            html += 'L ' + (p1[0] + normal[0]) + ' ' + (p1[1] + normal[1]) + ' ';
            html += 'L ' + (p1[0] - normal[0]) + ' ' + (p1[1] - normal[1]) + ' ';
            html += 'Z\' />';
        }

        html += `<path class="beam_${this.element_type.material} beam ${add_classes.join(' ')}" ${fill} d='`;
        html += 'M ' + (p0[0] - normal[0]) + ' ' + (p0[1] - normal[1]) + ' ';
        html += 'L ' + (p0[0] + normal[0]) + ' ' + (p0[1] + normal[1]) + ' ';
        html += 'L ' + (p1[0] + normal[0]) + ' ' + (p1[1] + normal[1]) + ' ';
        html += 'L ' + (p1[0] - normal[0]) + ' ' + (p1[1] - normal[1]) + ' ';
        html += 'Z\' />';

        return html;
    }

    draw_id_label(camera, storey) {
        return this._draw_id_label(camera, storey, camera.world_to_screen(cn_middle(this.vertices[0], this.vertices[1])));
    }

    //***********************************************************************************
    //**** Contains
    //***********************************************************************************
    contains(point, tolerance = 0) {
        var d = cn_sub(point, this.vertices[0]);
        var dir = cn_sub(this.vertices[1], this.vertices[0]);
        var l = cn_normalize(dir);
        if (l <= tolerance) return cn_dist(point, this.vertices[0]) <= tolerance
        var x = cn_dot(d, dir);
        if (x < -tolerance) return false;
        if (x > l + tolerance) return false;
        var y = cn_dot(d, cn_normal(dir));
        return (Math.abs(y) < 0.5 * this.element_type.width * 0.5 + tolerance);
    }

    //***********************************************************************************
    //**** Build 3D solid
    //***********************************************************************************
    build_solid(h, h0, h1) {
        var v0 = fh_clone(this.vertices[0]);
        var v1 = fh_clone(this.vertices[1]);
        if (v0[2] == 0) v0[2] = h0;
        else v0[2] += h;
        if (v1[2] == 0) v1[2] = h1;
        else v1[2] += h;
        var dz = fh_sub(v1, v0);
        var length = fh_normalize(dz);
        var dx = fh_cross(dz, [0, 0, 1]);
        fh_normalize(dx);
        var dy = fh_cross(dz, dx);
        fh_normalize(dy);

        var ori = fh_clone(v0);
        ori = fh_sub(ori, fh_mul(dx, 0.5 * this.element_type.width));

        var solid = this.element_type.build_solid(length);
        var matrix = new fh_matrix();
        matrix.load_axis(ori, dx, dy, dz);
        solid.apply_matrix(matrix);
        return solid;
    }

    build_3d_vertices(z, storey) {
        var vertices = [fh_clone(this.vertices[0]), fh_clone(this.vertices[1])];
        for (var k = 0; k < 2; k++) {
            if (vertices[k][2] == 0)
                vertices[k][2] = storey.compute_z_ceiling(vertices[k], false);
            vertices[k][2] += z;
        }
        return vertices;
    }

    /**
     * Builds an extruded poilygon
     * @param {number} h : height of the floor
     * @param {cn_storey} storey
     * @returns {fh_extruded_polygon}
     */
    build_extruded_polygon(h, storey) {
        var vertices = this.build_3d_vertices(h, storey);

        var dz = fh_sub(vertices[1], vertices[0]);
        var length = fh_normalize(dz);
        var dx = fh_cross(dz, [0, 0, 1]);
        fh_normalize(dx);
        var dy = fh_cross(dz, dx);
        fh_normalize(dy);

        var ori = fh_clone(vertices[0]);
        ori = fh_sub(ori, fh_mul(dx, 0.5 * this.element_type.width));

        var extruded_polygon = fh_extruded_polygon.build_extrusion(this.element_type.build_footprint(), [0, 0, length], this.element_type.get_color());

        var matrix = new fh_matrix();
        matrix.load_axis(ori, dx, dy, dz);
        extruded_polygon.matrix = matrix;
        return extruded_polygon;
    }

    /**
     * Returns a polygon that represents the footprint of the part of the beam that is below z.
     * Used to remove beam areas in space metrics and area contxts.
     * @param {number} z
     * @param {cn_storey} storey
     * @returns {fh_polygon}
     */
    get_low_footprint(z, storey) {
        const polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);
        var vertices = this.build_3d_vertices(0, storey);
        var dz = fh_sub(vertices[1], vertices[0]);
        var length = fh_normalize(dz);
        var dx = fh_cross(dz, [0, 0, 1]);
        fh_normalize(dx);
        var dy = fh_cross(dz, dx);
        fh_normalize(dy);
        dy = cnx_mul(dy, this.element_type.thickness);
        vertices[0] = cnx_add(vertices[0], dy);
        vertices[1] = cnx_add(vertices[1], dy);

        if (vertices[0][2] > z && vertices[1][2] > z)
            return polygon;
        for (var k = 0; k < 2; k++) {
            if (vertices[k][2] <= z) continue;
            const dir = cnx_sub(vertices[k], vertices[1 - k]);
            const l = cnx_normalize(dir);
            const x = l * (z - vertices[1 - k][2]) / (vertices[k][2] - vertices[1 - k][2]);
            if (x <= 0) return polygon;
            vertices[k] = cnx_add(vertices[1 - k], cnx_mul(dir, x));
        }
        dx = cnx_mul(dx, 0.5 * this.element_type.width);
        const contour = [];
        contour.push(cnx_add(vertices[0], dx));
        contour.push(cnx_add(vertices[1], dx));
        contour.push(cnx_sub(vertices[1], dx));
        contour.push(cnx_sub(vertices[0], dx));
        polygon.add_contour(contour);
        return polygon;
    }

    /**
     * Returns the contour of the beam
     * @returns {number[][]}
     */
    compute_contour() {
        const dir = cn_sub(this.vertices[1], this.vertices[0]);
        if (cn_normalize(dir) < 0.001) return [];
        const nor = cn_normal(dir);
        const contour = [];
        contour.push(cn_add(this.vertices[0], cn_mul(nor, 0.5 * this.element_type.width)));
        contour.push(cn_add(this.vertices[1], cn_mul(nor, 0.5 * this.element_type.width)));
        contour.push(cn_add(this.vertices[1], cn_mul(nor, -0.5 * this.element_type.width)));
        contour.push(cn_add(this.vertices[0], cn_mul(nor, -0.5 * this.element_type.width)));
        return contour;
    }

    //***********************************************************************************
    //**** Vertex operation
    //***********************************************************************************
    /**
     * Performe a vertex operation
     * @param {function} operation : the function that transforms vertices
     */
    vertex_operation(operation) {
        operation(this.vertices[0]);
        operation(this.vertices[1]);
    }

    //***********************************************************************************
    /**
     * Returns bounding box
     * @returns {cn_box}
     */
    get_bounding_box() {
        var box = new cn_box();
        box.enlarge_point(this.vertices[0]);
        box.enlarge_point(this.vertices[1]);
        return box;
    }

    /**
     * Accept element visitor
     *
     * @param {cn_element_visitor} element_visitor
     */
    accept_visitor(element_visitor) {
        element_visitor.visit_beam(this);
    }

    /**
     * Returns all spaces linked to the beam.
     * @param {cn_scene} scene
     * @returns {cn_space[]}
     */
    get_spaces(scene) {
        return scene.get_spaces_on_path(this.vertices[0], this.vertices[1]).map(sd => sd.space);
    }

    compute_facing_count() {
        if (this.facing) this.facing.count++;
    }
    
    /**
     * TODO : derivate is element can be aligned
     * @returns {boolean}
     */
    is_alignable() {
        return true;
    }

    /**
     * TODO : derivate this to manage alignment
     * Returns current align center
     * @returns {number[]}
     */
    get_align_center() {
        return cn_middle(this.vertices[0],this.vertices[1]);
    }

    /**
     * TODO : derivate this to manage alignment
     * Returns true if center was successfully set
     * @param {number[]} p
     * @param {number[]} center
     * @param {import('../utils/cn_transaction_manager').cn_transaction_manager} transaction_manager
     * @returns {boolean}
     */
    set_align_center(p, center, transaction_manager = null) {
        if (transaction_manager)
            transaction_manager.push_item_set(this,"vertices");
        let offset = cn_sub(p,cn_middle(this.vertices[0],this.vertices[1]));
        this.vertices = this.vertices.map(v => cn_add(v,offset));
        return true;
    }
}

